import { Layout, Tag, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataTable } from "design-system/components/DataTable";
import { useFeatureFlags } from "shell/platform/contexts/FeatureFlagsContext";

import s from "./FeatureFlags.module.css";

const { Content } = Layout;
const { Title, Text } = Typography;

interface FeatureFlagRow {
  description: string;
  name: string;
  value: boolean;
}

const FeatureFlags = () => {
  const { t } = useTranslation(["common"]);
  const { flags } = useFeatureFlags();
  const [search, setSearch] = useState("");

  const allFlags = useMemo<FeatureFlagRow[]>(
    () =>
      Object.entries(flags)
        .map(([name, { value, description }]) => ({
          name,
          value,
          description: description || name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [flags],
  );

  const filteredFlags = useMemo<FeatureFlagRow[]>(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return allFlags;
    return allFlags.filter(
      flag =>
        flag.name.toLowerCase().includes(trimmed) ||
        flag.description.toLowerCase().includes(trimmed),
    );
  }, [allFlags, search]);

  const columns = [
    {
      title: t("components.featureFlags.featureKey"),
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (name: string) => <Text code>{name}</Text>,
    },
    {
      title: t("labels.enabled"),
      dataIndex: "value",
      key: "value",
      render: (value: boolean) => (
        <Tag color={value ? "green" : "default"}>
          {value ? t("labels.enabled") : t("labels.disabled")}
        </Tag>
      ),
    },
    {
      title: t("labels.description"),
      dataIndex: "description",
      key: "description",
    },
  ];

  return (
    <Layout className={s.container}>
      <Content className={s.content}>
        <Title level={1} className={s.title}>
          {t("components.mainMenu.featureFlags")}
        </Title>
        <Text className={s.subtitle}>
          {t("components.featureFlags.subtitle")}
        </Text>

        <DataTable
          columns={columns}
          rowKey="name"
          data={filteredFlags}
          searchInput={{
            placeholder: t("components.featureFlags.searchPlaceholder"),
            onChange: setSearch,
          }}
        />
      </Content>
    </Layout>
  );
};

export default FeatureFlags;
